import { Route } from 'react-router';

import { AssessmentTemplateEdit, AssessmentTemplateList } from './pages/assessment_templates';
import { CloudProvidersCreate, CloudProvidersEdit, CloudProvidersList } from './pages/cloud_providers';
import { ConnectorCreate, ConnectorEdit, ConnectorList } from './pages/connectors';
import { ReadyDocumentationList } from './pages/files';
import { FlexPackagesCreate, FlexPackagesEdit, FlexPackagesList } from './pages/flex_packages';
import { HistoryList } from './pages/history';
import { MitigationList, MitigationsCreate, MitigationsEdit } from './pages/mitigations';
import { ModuleContentCreate, ModuleContentList } from './pages/module_contents';
import { ModuleCreate, ModuleEdit, ModuleList, ModuleShow } from './pages/modules';
import { ParameterModelerList } from './pages/parameter_modeler';
import { PhaseEdit, PhaseList } from './pages/phases';
import { ReportTypeCreate, ReportTypeEdit, ReportTypeList } from './pages/report_types';
import { ScenarioCreate, ScenarioEdit, ScenarioList } from './pages/scenarios';
import { TaggedItemsCreate, TaggedItemsList } from './pages/tagged_items';
import { TagCreate, TagList } from './pages/tags';
import TagsEdit from './pages/tags/edit';
import { TagSetsCreate, TagSetsEdit, TagSetsList } from './pages/tagsets';
import { ScenarioTemplateList, ScenarioTemplateEdit } from './pages/templates';
import {
  ThreatInformedDefenseCreate,
  ThreatInformedDefenseEdit,
  ThreatInformedDefenseList,
  ThreatInformedDefenseShow
} from './pages/threat_informed_defense';
import { UnifiedMitigationList, UnifiedMitigationsCreate, UnifiedMitigationsEdit } from './pages/unified_mitigations';
import { VendorProductCreate, VendorProductEdit, VendorProductList } from './pages/vendor_products';
import { VendorCreate, VendorEdit, VendorList } from './pages/vendors';
import { NewCreate, NewEdit, NewList, NewShow } from './pages/whats_new';

// A new top-level route segment must not start with a prefix that the deployed
// app sends to Django. The cluster ingress (platform-infrastructure
// gitops/manifests/firedrill/core/templates/ingress-data.yml) and the on-prem
// firedrill-data urls.py match these by prefix, not by whole segment:
//   v1 v2 admin* api-auth api-token-auth auth confirm-email csrf downloads
//   global_configs health history internal login logout membership migrations
//   password_reset* set_password_complete static_django user version
// `^user` also captures /users, so a users page needs another name (/accounts). The
// `history` routes below are already captured: a refresh or deep link there is
// served by the API, not this app. The dev proxy (craco.config.js) forwards
// fewer prefixes, so a collision only shows once deployed.
export const routeConfig = {
  modules: {
    create: {
      element: <ModuleCreate />,
      url: '/_modules/modules/create'
    },
    edit: {
      element: <ModuleEdit />,
      url: '/_modules/modules/edit/:id'
    },
    list: {
      element: <ModuleList />,
      url: '/_modules/modules'
    },
    show: {
      element: <ModuleShow />,
      url: '/_modules/modules/show/:id'
    }
  },
  module_contents: {
    list: {
      element: <ModuleContentList />,
      url: '/_modules/module_contents'
    },
    create: {
      element: <ModuleContentCreate />,
      url: '/_modules/module_contents/create'
    }
  },
  assessment_templates: {
    list: {
      element: <AssessmentTemplateList />,
      url: '/assessments/assessment_templates'
    },
    edit: {
      element: <AssessmentTemplateEdit />,
      url: '/assessments/assessment_templates/edit/:id'
    }
  },
  report_types: {
    list: {
      element: <ReportTypeList />,
      url: '/reports/report_types'
    },
    edit: {
      element: <ReportTypeEdit />,
      url: '/reports/report_types/edit/:id'
    },
    create: {
      element: <ReportTypeCreate />,
      url: '/reports/report_types/create'
    }
  },
  scenarios: {
    list: {
      element: <ScenarioList />,
      url: '/content/scenarios'
    },
    create: {
      element: <ScenarioCreate />,
      url: '/content/scenarios/create'
    },
    edit: {
      element: <ScenarioEdit />,
      url: '/content/scenarios/edit/:id'
    }
  },
  mitigations: {
    list: {
      element: <MitigationList />,
      url: '/content/mitigations'
    },
    edit: {
      element: <MitigationsEdit />,
      url: '/content/mitigations/edit/:id'
    },
    create: {
      element: <MitigationsCreate />,
      url: '/content/mitigations/create'
    }
  },
  unified_mitigations: {
    list: {
      element: <UnifiedMitigationList />,
      url: '/content/unified_mitigations'
    },
    edit: {
      element: <UnifiedMitigationsEdit />,
      url: '/content/unified_mitigations/edit/:id'
    },
    create: {
      element: <UnifiedMitigationsCreate />,
      url: '/content/unified_mitigations/create'
    }
  },
  scenario_templates: {
    list: {
      element: <ScenarioTemplateList />,
      url: '/content/scenario_templates'
    },
    edit: {
      element: <ScenarioTemplateEdit />,
      url: '/content/scenario_templates/edit/:id'
    }
  },
  phases: {
    list: {
      element: <PhaseList />,
      url: '/content/phases'
    },
    edit: {
      element: <PhaseEdit />,
      url: '/content/phases/edit/:id'
    }
  },
  cloud_providers: {
    list: {
      element: <CloudProvidersList />,
      url: '/content/cloud_providers'
    },
    edit: {
      element: <CloudProvidersEdit />,
      url: '/content/cloud_providers/edit/:id'
    },
    create: {
      element: <CloudProvidersCreate />,
      url: '/content/cloud_providers/create'
    }
  },
  connectors: {
    list: {
      element: <ConnectorList />,
      url: '/integrations/connectors'
    },
    create: {
      element: <ConnectorCreate />,
      url: '/integrations/connectors/create'
    },
    edit: {
      element: <ConnectorEdit />,
      url: '/integrations/connectors/edit/:id'
    }
  },
  vendors: {
    list: {
      element: <VendorList />,
      url: '/integrations/vendors'
    },
    create: {
      element: <VendorCreate />,
      url: '/integrations/vendors/create'
    },
    edit: {
      element: <VendorEdit />,
      url: '/integrations/vendors/edit/:id'
    }
  },
  vendor_products: {
    list: {
      element: <VendorProductList />,
      url: '/integrations/vendor_products'
    },
    create: {
      element: <VendorProductCreate />,
      url: '/integrations/vendor_products/create'
    },
    edit: {
      element: <VendorProductEdit />,
      url: '/integrations/vendor_products/edit/:id'
    }
  },
  parameter_modeler: {
    list: {
      element: <ParameterModelerList />,
      url: '/integrations/parameter_modeler'
    }
  },
  tags: {
    list: {
      element: <TagList />,
      url: '/tagging/tags'
    },
    edit: {
      element: <TagsEdit />,
      url: '/tagging/tags/edit/:id'
    },
    create: {
      element: <TagCreate />,
      url: '/tagging/tags/create'
    }
  },
  tag_sets: {
    list: {
      element: <TagSetsList />,
      url: '/tagging/tag_sets'
    },
    edit: {
      element: <TagSetsEdit />,
      url: '/tagging/tag_sets/edit/:id'
    },
    create: {
      element: <TagSetsCreate />,
      url: '/tagging/tag_sets/create'
    }
  },
  tagged_items: {
    list: {
      element: <TaggedItemsList />,
      url: '/tagging/tagged_items'
    },
    create: {
      element: <TaggedItemsCreate />,
      url: '/tagging/tagged_items/create'
    }
  },
  flex_packages: {
    list: {
      element: <FlexPackagesList />,
      url: '/flex/flex_packages'
    },
    create: {
      element: <FlexPackagesCreate />,
      url: '/flex/flex_packages/create'
    },
    edit: {
      element: <FlexPackagesEdit />,
      url: '/flex/flex_packages/edit/:id'
    }
  },
  history: {
    list: {
      element: <HistoryList />,
      url: '/history'
    }
  },
  whats_new: {
    list: {
      element: <NewList />,
      url: '/_news/whats_new'
    },
    create: {
      element: <NewCreate />,
      url: '/_news/whats_new/create'
    },
    edit: {
      element: <NewEdit />,
      url: '/_news/whats_new/edit/:id'
    },
    show: {
      element: <NewShow />,
      url: '/_news/whats_new/show/:id'
    }
  },
  threat_informed_defense: {
    list: {
      element: <ThreatInformedDefenseList />,
      url: '/_news/threat_informed_defense'
    },
    create: {
      element: <ThreatInformedDefenseCreate />,
      url: '/_news/threat_informed_defense/create'
    },
    edit: {
      element: <ThreatInformedDefenseEdit />,
      url: '/_news/threat_informed_defense/edit/:id'
    },
    show: {
      element: <ThreatInformedDefenseShow />,
      url: '/_news/threat_informed_defense/show/:id'
    }
  },
  ready_documentation: {
    list: {
      element: <ReadyDocumentationList />,
      url: '/_files/ready_documentation'
    }
  }
};

export const loggedInRoutes = () => {
  return (
    <>
      <Route path="/_modules/modules">
        <Route index element={routeConfig.modules.list.element} />
        <Route path="create" element={routeConfig.modules.create.element} />
        <Route path="edit/:id" element={routeConfig.modules.edit.element} />
        <Route path="show/:id" element={routeConfig.modules.show.element} />
      </Route>
      <Route path="/_modules/module_contents">
        <Route index element={routeConfig.module_contents.list.element} />
        <Route path="create" element={routeConfig.module_contents.create.element} />
      </Route>
      <Route path="/assessments/assessment_templates">
        <Route index element={routeConfig.assessment_templates.list.element} />
        <Route path="edit/:id" element={routeConfig.assessment_templates.edit.element} />
      </Route>
      <Route path="/reports/report_types">
        <Route index element={routeConfig.report_types.list.element} />
        <Route path="create" element={routeConfig.report_types.create.element} />
        <Route path="edit/:id" element={routeConfig.report_types.edit.element} />
      </Route>
      <Route path="/content/scenarios">
        <Route index element={routeConfig.scenarios.list.element} />
        <Route path="create" element={routeConfig.scenarios.create.element} />
        <Route path="edit/:id" element={routeConfig.scenarios.edit.element} />
      </Route>
      <Route path="/content/mitigations">
        <Route index element={routeConfig.mitigations.list.element} />
        <Route path="create" element={routeConfig.mitigations.create.element} />
        <Route path="edit/:id" element={routeConfig.mitigations.edit.element} />
      </Route>
      <Route path="/content/unified_mitigations">
        <Route index element={routeConfig.unified_mitigations.list.element} />
        <Route path="create" element={routeConfig.unified_mitigations.create.element} />
        <Route path="edit/:id" element={routeConfig.unified_mitigations.edit.element} />
      </Route>
      <Route path="/content/scenario_templates">
        <Route index element={routeConfig.scenario_templates.list.element} />
        <Route path="edit/:id" element={routeConfig.scenario_templates.edit.element} />
      </Route>
      <Route path="/content/phases">
        <Route index element={routeConfig.phases.list.element} />
        <Route path="edit/:id" element={routeConfig.phases.edit.element} />
      </Route>
      <Route path="/content/cloud_providers">
        <Route index element={routeConfig.cloud_providers.list.element} />
        <Route path="create" element={routeConfig.cloud_providers.create.element} />
        <Route path="edit/:id" element={routeConfig.cloud_providers.edit.element} />
      </Route>
      <Route path="/integrations/connectors">
        <Route index element={routeConfig.connectors.list.element} />
        <Route path="create" element={routeConfig.connectors.create.element} />
        <Route path="edit/:id" element={routeConfig.connectors.edit.element} />
      </Route>
      <Route path="/integrations/vendors">
        <Route index element={routeConfig.vendors.list.element} />
        <Route path="create" element={routeConfig.vendors.create.element} />
        <Route path="edit/:id" element={routeConfig.vendors.edit.element} />
      </Route>
      <Route path="/integrations/vendor_products">
        <Route index element={routeConfig.vendor_products.list.element} />
        <Route path="create" element={routeConfig.vendor_products.create.element} />
        <Route path="edit/:id" element={routeConfig.vendor_products.edit.element} />
      </Route>
      <Route path="/integrations/parameter_modeler">
        <Route index element={routeConfig.parameter_modeler.list.element} />
      </Route>
      <Route path="/tagging/tags">
        <Route index element={routeConfig.tags.list.element} />
        <Route path="create" element={routeConfig.tags.create.element} />
        <Route path="edit/:id" element={routeConfig.tags.edit.element} />
      </Route>
      <Route path="/tagging/tag_sets">
        <Route index element={routeConfig.tag_sets.list.element} />
        <Route path="create" element={routeConfig.tag_sets.create.element} />
        <Route path="edit/:id" element={routeConfig.tag_sets.edit.element} />
      </Route>
      <Route path="/tagging/tagged_items">
        <Route index element={routeConfig.tagged_items.list.element} />
        <Route path="create" element={routeConfig.tagged_items.create.element} />
      </Route>
      <Route path="/flex/flex_packages">
        <Route index element={routeConfig.flex_packages.list.element} />
        <Route path="create" element={routeConfig.flex_packages.create.element} />
        <Route path="edit/:id" element={routeConfig.flex_packages.edit.element} />
      </Route>
      <Route path="/history">
        <Route index element={routeConfig.history.list.element} />
      </Route>
      <Route path="/_news/whats_new">
        <Route index element={routeConfig.whats_new.list.element} />
        <Route path="create" element={routeConfig.whats_new.create.element} />
        <Route path="edit/:id" element={routeConfig.whats_new.edit.element} />
        <Route path="show/:id" element={routeConfig.whats_new.show.element} />
      </Route>
      <Route path="/_news/threat_informed_defense">
        <Route index element={routeConfig.threat_informed_defense.list.element} />
        <Route path="create" element={routeConfig.threat_informed_defense.create.element} />
        <Route path="edit/:id" element={routeConfig.threat_informed_defense.edit.element} />
        <Route path="show/:id" element={routeConfig.threat_informed_defense.show.element} />
      </Route>
      <Route path="/_files/ready_documentation">
        <Route index element={routeConfig.ready_documentation.list.element} />
      </Route>
    </>
  );
};
